import React, { useState } from 'react';
import { Modal, Button, Form, Badge, ListGroup, InputGroup, Card } from 'react-bootstrap';
import type { AllergyResponse } from '../../../../../../Types/schema-helpers';
import { useLanguage } from '../../../../../../API/Context/LanguageContext';
import { useHealthBackgroundActions } from '../../../../../../API/Hooks/useHealthBackgroundActions';
import EnumSelect from '../../../../LoggedUserGeneral/ProfileOnboarding/Steps/Widgets/EnumSelect';
import { 
  ALLERGY_CATEGORY_MAP,
  ALLERGY_SEVERITY_MAP,
  ALLERGY_CRITICALITY_MAP,
  ALLERGY_STATUS_MAP, 
} from '../../../../../../ConfigFiles/maps/allergies_map';

export interface AllergyDetailModalProps {
  allergy: AllergyResponse | null;
  patientUsername?: string | undefined;
  show: boolean;
  onHide: () => void;
  isProfessional: boolean;
}

export const AllergyDetailModal: React.FC<AllergyDetailModalProps> = ({
  allergy,
  show,
  onHide,
  isProfessional,
}) => {
  const { lan } = useLanguage();
  const currentLanKey = (lan as number) === 0 ? 0 : 1;

  const {
    updateAllergy,
    addAllergyNote,
    addAllergyReaction,
    deleteAllergy,
    loading,
  } = useHealthBackgroundActions();

  const [noteText, setNoteText] = useState('');
  const [reactionText, setReactionText] = useState('');

  if (!allergy) return null;

  const reactions = allergy.reactions ?? [];
  const notes = allergy.notes ?? [];

  const handleStatusChange = async (newStatus: number) => {
    await updateAllergy(allergy.allergy_id, {
      category: allergy.category,
      severity: allergy.severity,
      criticality: allergy.criticality,
      status: newStatus as any,
    });
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    await addAllergyNote(allergy.allergy_id, { text: noteText.trim() });
    setNoteText('');
  };

  const handleAddReaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reactionText.trim()) return;
    await addAllergyReaction(allergy.allergy_id, { text: reactionText.trim() });
    setReactionText('');
  };

  const handleDelete = async () => {
    const confirmMsg = currentLanKey === 0 ? '¿Desea eliminar este registro de alergia?' : 'Delete this allergy record?';
    if (window.confirm(confirmMsg)) {
      await deleteAllergy(allergy.allergy_id);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <span>{allergy.allergen}</span>
          <Badge bg="secondary" className="fs-6 fw-normal">
            {ALLERGY_CATEGORY_MAP.find((c) => c.id === allergy.category)?.labels[currentLanKey]}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex flex-column gap-3">
        {/* Status & Severity Controls */}
        <Card className="bg-light border-0 p-3">
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label className="small fw-bold text-muted">
                {currentLanKey === 0 ? 'Estado' : 'Status'}
              </Form.Label>
              <EnumSelect
                value={allergy.status}
                options={ALLERGY_STATUS_MAP}
                onChange={handleStatusChange}
                disabled={!isProfessional || loading}
                size="sm"
              />
            </div>
            <div className="col-md-4">
              <Form.Label className="small fw-bold text-muted">
                {currentLanKey === 0 ? 'Severidad' : 'Severity'}
              </Form.Label>
              <div className="fw-semibold">
                {ALLERGY_SEVERITY_MAP.find((s) => s.id === allergy.severity)?.labels[currentLanKey]}
              </div>
            </div>
            <div className="col-md-4">
              <Form.Label className="small fw-bold text-muted">
                {currentLanKey === 0 ? 'Criticidad' : 'Criticality'}
              </Form.Label>
              <div className="fw-semibold">
                {ALLERGY_CRITICALITY_MAP.find((c) => c.id === allergy.criticality)?.labels[currentLanKey]}
              </div>
            </div>
          </div>
        </Card>

        {/* Reactions Section */}
        <div>
          <h6 className="fw-bold">{currentLanKey === 0 ? 'Reacciones' : 'Reactions'}</h6>
          {reactions.length === 0 ? (
            <p className="text-muted small fst-italic">{currentLanKey === 0 ? 'Sin reacciones registradas' : 'No reactions recorded'}</p>
          ) : (
            <ListGroup variant="flush" className="mb-2">
              {reactions.map((r) => (
                <ListGroup.Item key={r.reaction_id} className="px-0 py-1 d-flex justify-content-between align-items-center">
                  <span>• {r.text}</span>
                  <small className="text-muted">{new Date(r.created_on).toLocaleDateString()}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {isProfessional && (
            <Form onSubmit={handleAddReaction} className="mt-2">
              <InputGroup size="sm">
                <Form.Control
                  placeholder={currentLanKey === 0 ? 'Agregar reacción...' : 'Add reaction...'}
                  value={reactionText}
                  onChange={(e) => setReactionText(e.target.value)}
                />
                <Button type="submit" variant="outline-primary" disabled={loading || !reactionText.trim()}>
                  <i className="bi bi-plus-lg" />
                </Button>
              </InputGroup>
            </Form>
          )}
        </div>

        {/* Clinical Notes Section */}
        <div>
          <h6 className="fw-bold">{currentLanKey === 0 ? 'Notas Clínicas' : 'Clinical Notes'}</h6>
          {notes.length === 0 ? (
            <p className="text-muted small fst-italic">{currentLanKey === 0 ? 'Sin notas registradas' : 'No notes recorded'}</p>
          ) : (
            <ListGroup variant="flush" className="mb-2">
              {notes.map((n) => (
                <ListGroup.Item key={n.note_id} className="px-0 py-1 bg-transparent">
                  <div className="small">{n.text}</div>
                  <small className="text-muted">{new Date(n.created_on).toLocaleDateString()}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {isProfessional && (
            <Form onSubmit={handleAddNote} className="mt-2">
              <InputGroup size="sm">
                <Form.Control
                  placeholder={currentLanKey === 0 ? 'Agregar nota clínica...' : 'Add clinical note...'}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <Button type="submit" variant="outline-primary" disabled={loading || !noteText.trim()}>
                  <i className="bi bi-plus-lg" />
                </Button>
              </InputGroup>
            </Form>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        {isProfessional ? (
          <Button variant="outline-danger" size="sm" onClick={handleDelete} disabled={loading}>
            <i className="bi bi-trash me-1" />
            {currentLanKey === 0 ? 'Eliminar' : 'Delete'}
          </Button>
        ) : <div />}
        <Button variant="secondary" size="sm" onClick={onHide}>
          {currentLanKey === 0 ? 'Cerrar' : 'Close'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};